import {
  DISPOSABLE_EMAIL_ERROR,
  PUBLIC_EMAIL_ERROR,
  RADAR_BLOCK_LOGIN_ERROR,
  RADAR_BLOCK_SIGNUP_ERROR,
} from "../auth-constants";

/**
 * The fixed set of analytics-safe auth error codes. Keeping this a closed union
 * (rather than `string`) is the same finite-domain-field lockdown the codebase
 * applies at public tool boundaries — here the boundary is the PostHog analytics
 * schema. It lets `AUTH_FAILURE_KIND` be exhaustively typed so a new code can't
 * be added without also deciding whether it should page. See BRE-4751.
 *
 * Every member is emitted by `normalizeAuthError()` below, feeding the
 * `error_code` property.
 */
export type AuthErrorCode =
  | "connection_lost"
  | "invalid_credentials"
  | "rate_limited"
  | "sso_required"
  | "email_not_verified"
  | "account_exists"
  | "expired"
  | "mfa_error"
  | "org_error"
  | "radar_blocked"
  | "public_email_domain"
  | "disposable_email_domain"
  | "unhandled_pending_action"
  | "unknown";

/**
 * The severity bucket for an auth failure. This is the single property Slack
 * alert destinations should filter on:
 *
 *   - `policy_block` — the product deliberately rejected the attempt (Radar
 *     blocked a bot, a personal/throwaway email was refused). This is the
 *     system SUCCEEDING. Dashboard it; NEVER page on it.
 *   - `user_error`   — the user can fix it and retry (bad password, wrong
 *     credentials, expired code, org needs SSO). Expected and high-volume.
 *   - `system_error` — an outcome we did not intend. The ONLY bucket that
 *     should reach #prod-alerts.
 *
 * Guiding principle: alert on outcomes we did not intend; dashboard the ones we
 * did.
 */
export type AuthFailureKind = "policy_block" | "user_error" | "system_error";

/**
 * The authoritative code → kind mapping. Defined once and shared by every auth
 * failure emission site (signup, login, invitation) so login and signup can't
 * classify the same code differently. Exhaustive over `AuthErrorCode` — adding
 * a code to the union without classifying it here is a compile error.
 */
export const AUTH_FAILURE_KIND: Record<AuthErrorCode, AuthFailureKind> = {
  // policy_block — the anti-abuse / eligibility system working as designed.
  radar_blocked: "policy_block",
  public_email_domain: "policy_block",
  disposable_email_domain: "policy_block",
  // user_error — recoverable by the user; expected, not actionable for us.
  invalid_credentials: "user_error",
  rate_limited: "user_error",
  sso_required: "user_error",
  email_not_verified: "user_error",
  account_exists: "user_error",
  expired: "user_error",
  // system_error — the only bucket that should page. connection_lost is here
  // because a spike is a genuine signal that our servers are unreachable.
  connection_lost: "system_error",
  mfa_error: "system_error",
  org_error: "system_error",
  // The server returned an interstitial the form has no step for. The user is
  // stuck at that point, so this must page rather than sit in a dashboard.
  unhandled_pending_action: "system_error",
  unknown: "system_error",
};

/**
 * Map any auth analytics `error_code` to its `failure_kind`.
 *
 * Accepts a raw string (not just `AuthErrorCode`) because a couple of call
 * sites emit dynamic codes such as `pending_action_mfa-challenge` — those are
 * genuine misconfigurations we DO want surfaced, so the fallback is deliberately
 * `system_error`, never a silent drop into a benign bucket.
 */
export function authFailureKind(code: string): AuthFailureKind {
  return AUTH_FAILURE_KIND[code as AuthErrorCode] ?? "system_error";
}

/**
 * Normalize auth error messages to a fixed set of analytics-safe codes.
 * Prevents PII and high-cardinality strings from flowing into PostHog.
 *
 * The raw user-facing message should still be shown in the UI via setError().
 */
export function normalizeAuthError(error: string): AuthErrorCode {
  const e = error.toLowerCase();

  // Policy blocks come first, matched against the exact canonical copy (defined
  // once in auth-constants.ts). This keeps deliberate product rejections out of
  // the "unknown" → system_error bucket, and because we match the constant
  // itself, a future copy edit updates the match automatically. See BRE-4751.
  if (e === RADAR_BLOCK_SIGNUP_ERROR.toLowerCase() || e === RADAR_BLOCK_LOGIN_ERROR.toLowerCase())
    return "radar_blocked";
  if (e === PUBLIC_EMAIL_ERROR.toLowerCase())
    return "public_email_domain";
  if (e === DISPOSABLE_EMAIL_ERROR.toLowerCase())
    return "disposable_email_domain";

  if (e.includes("connection lost") || e.includes("network") || e.includes("refresh the page"))
    return "connection_lost";
  if (e.includes("invalid email") || e.includes("invalid code") || e.includes("invalid credentials"))
    return "invalid_credentials";
  if (e.includes("too many") || e.includes("rate"))
    return "rate_limited";
  if (e.includes("sso"))
    return "sso_required";
  if (e.includes("verify your email") || e.includes("email not verified"))
    return "email_not_verified";
  if (e.includes("already exists") || e.includes("duplicate"))
    return "account_exists";
  if (e.includes("expired"))
    return "expired";
  if (e.includes("mfa") || e.includes("authenticator") || e.includes("totp"))
    return "mfa_error";
  if (e.includes("organization"))
    return "org_error";

  return "unknown";
}
